import axios from 'axios';
import store from '../store/store';
import { setCredentials, logout } from '../features/authSlice';

const AUTH_BASE_URL = 'https://auth.doramahype.ru';

const authAxios = axios.create({
  baseURL: AUTH_BASE_URL,
  withCredentials: true, // для httpOnly refresh_token cookie
});

// Флаг чтобы не запускать несколько refresh одновременно
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, accessToken = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(accessToken);
  });
  failedQueue = [];
};

// Единая точка входа для refresh — используется в interceptor, baseQuery и App.js.
// Если refresh уже идёт — встаём в очередь и ждём результата (не делаем второй HTTP запрос).
export const getNewToken = () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }
  isRefreshing = true;
  return authAxios.post('/auth/refresh')
    .then((res) => {
      const token = res.data?.data?.access_token;
      const user = res.data?.data?.user || null;
      if (!token) throw new Error('no token');
      // Обновляем Redux + localStorage
      const state = store.getState();
      store.dispatch(setCredentials({ user: user || state.auth.user, accessToken: token }));
      // Сбрасываем флаг ДО processQueue чтобы избежать race condition:
      // если после processQueue придёт новый запрос — он не запустит лишний refresh
      isRefreshing = false;
      processQueue(null, token);
      return { token, user };
    })
    .catch((err) => {
      isRefreshing = false;
      processQueue(err, null);
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        store.dispatch(logout());
      }
      throw err;
    });
};

authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Только для 401, и только один раз
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Не пытаемся refresh на самих auth-эндпоинтах
    const skipRefreshPaths = ['/auth/refresh', '/auth/logout', '/auth/telegram', '/auth/complete-register'];
    if (skipRefreshPaths.some((p) => originalRequest.url?.includes(p))) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const { token } = await getNewToken();
      originalRequest.headers['Authorization'] = `Bearer ${token}`;
      return authAxios(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export const authAPI = {
  // Получить текущего пользователя по access token
  getMe: (accessToken) =>
    authAxios.get('/auth/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),

  // Обновить access token через refresh cookie
  refresh: () =>
    authAxios.post('/auth/refresh'),

  // Выйти (отозвать refresh token)
  logout: () =>
    authAxios.post('/auth/logout'),

  // Telegram login (через Login Widget)
  telegramLogin: (telegramData) =>
    authAxios.post('/auth/telegram', telegramData),

  // Google — просто редирект, URL формируем здесь
  getGoogleLoginUrl: () => `${AUTH_BASE_URL}/auth/google`,

  // Google — привязка к существующему аккаунту (передаём токен в query)
  getGoogleConnectUrl: (accessToken) => `${AUTH_BASE_URL}/auth/connect/google?token=${encodeURIComponent(accessToken)}`,

  // Telegram — привязка к существующему аккаунту
  telegramConnect: (accessToken, telegramData) =>
    authAxios.post('/auth/connect/telegram', telegramData, {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),

  // Проверить доступность никнейма
  checkLogin: (login) =>
    authAxios.get(`/auth/check-login?login=${encodeURIComponent(login)}`),

  // Завершить регистрацию: сохранить никнейм по pending_token
  completeRegister: (pendingToken, login) =>
    authAxios.post('/auth/complete-register', { pending_token: pendingToken, login }),
};

export default authAxios;
